import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient, ApiError } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { workspaceKey } from '../lib/workspaceQueryKey';
import { ASSISTANT_NOT_AVAILABLE_CODE, markAssistantUnavailable } from '../lib/useCanUseAssistant';
import { AI_CONSENT_REQUIRED_CODE, hasAiConsent, type ChatThread, type ChatThreadSummary } from '../api/types';
import { useTourOptional } from '../tour/TourProvider';
import { readPanelState, writePanelState } from './panelStorage';
import { useMediaQuery, XL_QUERY } from '../lib/useMediaQuery';
import type { AiBudget, BillingAiUsage, Message, MessageMeta, QueryResponse } from './types';
import { ASSISTANT_CANCEL_TIMEOUT_MS, ASSISTANT_QUERY_TIMEOUT_MS } from './constants';
import { askStreamed, StreamFailure, parseAssistantConfig, STREAMING_OFF, type AssistantConfig } from './stream';

/** Shown in the rename dialog when the server refused the rename (steward-app#2622). */
export const RENAME_FAILED_MESSAGE = "We couldn't rename that chat. Please try again.";
/** Shown when the chat changed between reading it and renaming it (a 409). */
export const RENAME_CONFLICT_MESSAGE = 'This chat changed while you were editing. Please try again.';

/**
 * One conversation, however you reach it.
 *
 * The chat used to be local state inside the `/ask` page, which is fine while the page is
 * the only surface. docs/24 adds a second one (a dockable side panel openable from any
 * route), and two surfaces owning their own copies would disagree about consent, the active
 * thread, or remaining quota the moment either changed. So the state lives here and the
 * surfaces render it.
 *
 * Mounted inside `Layout`: below `WorkspaceProvider` (it reads `profile` and
 * `acknowledgeAiConsent`) and inside the router (it reads `pathname`).
 */

export interface AssistantContextValue {
  // Conversation
  messages: Message[];
  activeThreadId: string | null;
  threads: ChatThreadSummary[];
  sending: boolean;
  /**
   * The saved thread the question in flight will be written to, captured when it was sent, or
   * null when nothing is in flight (or it is the first question of a new chat, which has no
   * saved thread yet). NOT `activeThreadId`: opening another chat or starting a new one while a
   * question is out moves that, but the server still saves the turn to this one. A rename of it
   * before the save lands would make the save fail (handlers/assistantThreads.ts `rename`), so
   * `/ask` offers no rename on it.
   */
  sendingThreadId: string | null;
  sendStartedAt: number | null;
  loadingThread: boolean;
  /** The draft. Shared so it survives moving between surfaces (docs/24). */
  input: string;
  setInput: (v: string) => void;
  // Metering
  budget: AiBudget | null;
  limitReached: boolean;
  // Consent
  consentAcknowledged: boolean;
  /** False until `GET /me` settles AND the workspace kind is known — surfaces gate on this so the
   *  modal doesn't flash, and so the wrong variant is never offered. */
  profileLoaded: boolean;
  /** `/me` has settled and there IS an active membership, but its packs read is degraded, so the
   *  workspace kind is unresolved and no consent dialog can be offered. Surfaces show a retry in
   *  its place. False when there is no active membership at all (for example a member whose only
   *  workspace is suspended, on `/select-workspace`): a retry cannot fix that, so there is no
   *  notice, no dialog and no refetch, and send refuses as before. */
  kindLoadFailed: boolean;
  /** Re-reads `/me`. */
  retryKind: () => Promise<void>;
  acceptConsent: () => Promise<void>;
  // Actions
  send: (text?: string) => Promise<void>;
  /** Stops the question in flight: the question goes back into the input, no error is shown,
   *  and a late answer is dropped (steward-app#2547). A no-op unless `canStop` is true, which is
   *  only on a turn (firm or household) that is using the streaming transport.
   *
   *  How: it sends `POST /assistant/query/cancel` with the turn's `runId`, fire-and-forget, and
   *  then aborts the fetch. The cancel is what stops the server: the dev proof of 2026-10-26
   *  (docs/41 §8 step 4) found that closing the stream never reaches the function through the
   *  REST gateway, so an abort alone would leave the run going and the exchange saved. The
   *  cancel can go before the stream's `start` line, because the `runId` is minted at send
   *  time. A cancel that fails shows nothing: the person asked to stop, and the screen has.
   *
   *  Why streamed only: every conversation is saved (steward-app#1817), and the buffered route
   *  never checks for a cancel (docs/41 §5.2), so a buffered Stop and a resend could save the
   *  question twice or start a second thread. A turn that falls back to the buffered route
   *  (config off or failed to load, or a stream that never opened) therefore offers no Stop,
   *  and loses it if the fallback happens mid-turn.
   *
   *  What a streamed Stop does NOT guarantee: the stream route checks for a cancel between model
   *  iterations, before each tool and once more just before it saves, each a separate read. A
   *  cancel that lands after that last check, or whose write or read fails, leaves the answer
   *  saved. The window is now the save itself; it is not zero. */
  stop: () => void;
  /** True while the turn in flight is one `stop` will act on: a turn (firm or household) that is
   *  using the streaming transport. Surfaces offer the Stop control only when this is true. */
  canStop: boolean;
  startNewChat: () => void;
  openThread: (id: string) => Promise<void>;
  deleteThread: (id: string) => Promise<void>;
  /**
   * Rename a saved chat (steward-app#2622). Resolves once the server has stored the title and
   * rejects with an `Error` whose message is the sentence to show: the conflict sentence when
   * the chat changed since it was read (a 409), the general failure sentence otherwise. The
   * thread list is refetched either way, so a conflict shows the current title behind the
   * dialog.
   */
  renameThread: (id: string, title: string) => Promise<void>;
  /**
   * Whether this conversation is saved, which drives the thread list and history controls.
   * True in every workspace since the founder ruling of 2026-10-26 (steward-app#1817): firm
   * conversations are saved under the household rule. It was false in a firm's own workspace
   * from steward-app#1815 until then.
   */
  savesChats: boolean;
  /**
   * The active workspace is an advisory firm's own (the provider's `firmConversation` prop).
   * Picks the firm variant of the consent copy. Never decides persistence, and does not by
   * itself offer Stop (see `canStop`).
   */
  firmConversation: boolean;
  // Panel (docs/24). `openPanel` takes no question: the only caller that would ever pass
  // one is #507's option A, which the panel closed as unnecessary. A parameter with no
  // caller is the dead plumbing this work removed in T2.
  /** Open **and** wide enough to dock. Never true below `xl`, so no surface has to
   *  re-derive the breakpoint to know whether a panel is on screen. */
  panelOpen: boolean;
  /** Whether this viewport can dock a panel at all (docs/24 P4). Below it, every entry
   *  point navigates to `/ask` instead. */
  canDockPanel: boolean;
  openPanel: () => void;
  closePanel: () => void;
}

export const AssistantContext = createContext<AssistantContextValue | null>(null);

/**
 * A value that belongs to exactly one workspace, and carries which one.
 *
 * The provider outlives a workspace switch, so every piece of state it holds is state for a
 * particular family. Holding the family alongside the value (rather than beside it, in a
 * second variable a later commit clears) is what makes "family A's value read under family
 * B's id" unrepresentable rather than merely unlikely — see steward-app#859 and #862.
 */
interface WorkspaceHeld<T> {
  value: T;
  workspaceId: string | null;
}

/**
 * The held value, or null when it belongs to some other workspace than the one on screen.
 *
 * No workspace on screen resolves to nothing, deliberately: `null === null` would otherwise
 * make "nobody" an identity a held value can match, and a member-less user sent to the picker
 * would be shown the usage of the family they no longer have.
 */
function valueForWorkspace<T>(held: WorkspaceHeld<T> | null, workspaceId: string | null): T | null {
  if (!held || !workspaceId) return null;
  return held.workspaceId === workspaceId ? held.value : null;
}

export interface AssistantProviderProps {
  children: React.ReactNode;
  /**
   * The active workspace is an advisory firm's own (steward-app#1815). Its conversation is
   * saved exactly as a household one is, since the founder ruling of 2026-10-26
   * (steward-app#1817, replacing the 2026-09-25 ruling that it was never saved): the façade
   * returns a thread id, the thread list and resume work the same, and the prior turns come
   * from the stored thread on the server, so no `history` is sent. What this still decides is
   * the consent variant, the strict rendering of answers and whether Stop is offered.
   *
   * A hint, not the gate: the façade decides the kind from the workspace's packs, and refuses
   * to continue a thread of the other kind (it starts a new one), so a stale value here during
   * a switch costs at most a turn saved as a new thread.
   */
  firmConversation?: boolean;
  /**
   * Whether the workspace is a firm's or a family's cannot be told yet (steward-app#2523).
   * `firmConversation` reads `false` in that state, so without this a firm user holding only a
   * family acknowledgment would pass the household gate and could send a question the server
   * answers as a firm's. While true, consent reads as not acknowledged and no dialog is offered.
   */
  kindUnresolved?: boolean;
}

export function AssistantProvider({ children, firmConversation = false, kindUnresolved = false }: AssistantProviderProps) {
  const token = useAccessToken();
  const { pathname } = useLocation();
  const { profile, acknowledgeAiConsent, activeWorkspaceId, activeMembership, refresh } = useWorkspace();
  const qc = useQueryClient();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Read only through `sending`, so every path that ends a send (answer, failure, Stop, a
  // workspace switch) releases it without clearing this.
  const [sendThreadId, setSendThreadId] = useState<string | null>(null);
  // Whether the turn in flight can be stopped (`canStop`): set at send time, cleared on the
  // buffered fallback and wherever `sending` is cleared.
  const [stoppable, setStoppable] = useState(false);
  const [sendStartedAt, setSendStartedAt] = useState<number | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const nextId = useRef(1);

  // The thread pointer (#862) and the meter (#859) were bare values that the switch reset
  // below cleared one commit AFTER `activeWorkspaceId` changed. Anything reading both in
  // that commit paired one family's value with another family's id: the persist effect
  // wrote family A's thread id stamped with family B's workspace id, and the meter could
  // seed family A's numbers as family B's. Both are now held with their workspace and
  // resolved in RENDER, so the mismatched pair has no commit to exist in — the reset still
  // clears them, but correctness no longer depends on it winning a race.
  const [heldThread, setHeldThread] = useState<WorkspaceHeld<string> | null>(null);
  const [heldBudget, setHeldBudget] = useState<WorkspaceHeld<AiBudget> | null>(null);
  const activeThreadId = valueForWorkspace(heldThread, activeWorkspaceId);
  const budget = valueForWorkspace(heldBudget, activeWorkspaceId);

  // Read once, at mount: what the panel looked like when the user last left (docs/24 P9).
  const restored = useRef(readPanelState());
  const [panelRequested, setPanelRequested] = useState(() => restored.current.open);

  // The breakpoint lives here, not in each surface, so "is a panel on screen" has exactly
  // one answer. Below `xl` a remembered-open panel must not count as open at all: nothing
  // renders it, so it would be an invisible flag the user cannot close — one that still
  // engages the queries below and puts two requests on every cold load for a phone user who
  // will never see a panel.
  //
  // `/ask` is the third term, and it is *derived* rather than a write (docs/24 P3, amended):
  // the page IS the assistant, so docking a second copy of the open conversation beside it is
  // nonsense — but that is a fact about where you are standing, not a decision to close the
  // panel. Clearing `panelRequested` on arrival, which is what this used to do, destroyed the
  // only record that a panel had been open, so browser Back landed you on the page you left
  // with the panel gone and no way to tell it had ever been there. Derived, Back re-docks it
  // on the conversation you were already in (the provider owns that state, not the surfaces).
  const canDockPanel = useMediaQuery(XL_QUERY);
  const panelOpen = panelRequested && canDockPanel && pathname !== '/ask';

  // Whether the remembered conversation has been dealt with — fetched, refused, or there was
  // none. State rather than a ref because the persist effect below has to wait on it, and a
  // ref settling silently would leave that write sitting on stale inputs.
  const [rehydrateSettled, setRehydrateSettled] = useState(
    () => !(restored.current.open && restored.current.threadId),
  );

  // Whether this session has touched the assistant at all. It gates the two queries below,
  // and it exists for one reason: this provider mounts on EVERY authenticated route, while
  // the queries it owns used to mount only when the Ask page did. Without the gate, lifting
  // the state would silently put a /billing/config and an /assistant/threads request on every
  // page load in the app, for every user, including those who never open the assistant.
  // Sticky once set — leaving /ask (or closing the panel) should not un-engage a live
  // conversation. A restored-open panel counts as engagement (it is on screen asking to be
  // used, and its meter and history have to be real), but only once it is actually docked,
  // which is why this seeds `false` and the effect decides.
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (pathname === '/ask' || panelOpen) setEngaged(true);
  }, [pathname, panelOpen]);

  // Fail-closed has a cost: with the kind unresolved after `/me` has settled, the user gets no
  // dialog and a disabled composer, and `/me` would not be read again for minutes. So the first
  // time an engaged surface finds itself there, read `/me` once more, and expose a retry for
  // when that does not clear it (steward-app#2523). Only for a degraded read of a membership that
  // exists: not while `/me` is still loading (`profile` is absent), and not with no membership.
  const kindLoadFailed = kindUnresolved && !!profile && !!activeMembership;
  const retriedKindRef = useRef(false);
  const retryKind = useCallback(async () => { await refresh?.(); }, [refresh]);
  useEffect(() => {
    if (!kindLoadFailed) { retriedKindRef.current = false; return; }
    if (!engaged || retriedKindRef.current) return;
    retriedKindRef.current = true;
    void retryKind();
  }, [kindLoadFailed, engaged, retryKind]);

  // Everything above belongs to ONE workspace, and this provider outlives a switch.
  //
  // `switchWorkspace` deliberately stays on the same page (WorkspaceProvider), so `Layout`
  // never unmounts and neither do we. Without this, workspace A's transcript renders under
  // workspace B, the next send posts A's `threadId` under B's workspace header (the façade
  // drops it, so the user silently starts a new thread with visible history that isn't in
  // it), and `budget` never re-seeds because the seed effect bails once it has a value —
  // switching from a capped workspace to an uncapped one would leave the assistant disabled.
  // (Since #859 the meter and the thread pointer also carry the family they belong to, so
  // neither can be READ under another one whatever this block does. The clears stay, and the
  // reason is the RETURN TRIP: left in place, A -> B -> A would resurrect A's thread pointer
  // and A's meter against a transcript that has been emptied twice, and the next send would
  // post A's `threadId` into a conversation the user can no longer see.)
  //
  // Mirrors the SHAPE of WorkspaceProvider's own `resetQueries` reset exactly: the same
  // `undefined` initial sentinel so the first render doesn't reset, the same truthy check so
  // a transient null (no resolvable membership) isn't treated as a switch, and — since #848 —
  // the same LAYOUT phase. (The predicate itself is WorkspaceProvider's business; what is
  // mirrored here is when this runs and what counts as a switch.)
  //
  // LAYOUT effect, not a passive one (steward-app#850) — this is the load-bearing line, so do
  // not "tidy" it back to useEffect. `messages` holds the previous family's financial Q&A,
  // including balances the assistant quoted back, and a passive effect runs after the commit
  // that has already put the NEW family's name in the header and sidebar. That leaves a
  // paintable frame in which one family's name sits above another family's conversation.
  //
  // WHY THE HOOK IS WHAT FIXES IT, stated carefully, because the tempting explanation (the
  // two phases schedule their updates on different LANES) is wrong — both re-render
  // promptly once their effect has run. What differs is when the effect runs, and the line
  // it falls on is a TASK boundary, not a lane. React flushes a click's sync work in a
  // microtask: it renders, commits, and runs layout effects there, then hands passive
  // effects to the Scheduler, which is a MessageChannel MACROtask. The browser's paint sits
  // between the two. So clearing from the layout phase lands on the near side of the paint;
  // clearing from the passive phase lands on the far side of it. Measured, not inferred:
  // AssistantProvider.switchReset.test.tsx models exactly that boundary (real DOM click,
  // then drain microtasks only) and reds when this is reverted to `useEffect`.
  //
  // Note this is a DIFFERENT mechanism from the sibling fix in WorkspaceProvider, even
  // though the one-word change is identical. There the thing being outrun is React Query's
  // `notifyManager` timer; here it is React's own passive-effect task. That is also why the
  // sibling's test technique (read the DOM on the line after `fireEvent.click`) does not
  // transfer — see the test's docblock.
  //
  // SCOPE OF THE GUARANTEE, stated conservatively: it is pinned by a test for a switch
  // originating in a discrete event, which is every path this is about (WorkspaceSwitcher,
  // the advisor Clients page's "Open", SelectWorkspace). It very likely also holds for an
  // async-continuation switch (NewWorkspaceDialog's `await refresh()`), because a layout
  // effect's `setState` is flushed at the end of the same commit whatever started it — but
  // nothing here asserts that, so do not lean on it. Note the async case IS real residue for
  // WorkspaceProvider (#852) precisely because `resetQueries` has to wait for a timer that
  // this state update does not.
  //
  // AND THIS ONLY CLEARS WHAT THIS PROVIDER OWNS. The thread rail's titles
  // (['assistant-threads'], derived from the previous family's questions) and the usage meter
  // (['billing-config']) are React Query state on workspace-agnostic keys, so they are still
  // the sibling reset's job. Revert WorkspaceProvider's layout effect and the previous
  // family's thread titles paint under the new family's name even with this fix in place.
  //
  // Safe because this SPA has no SSR path (client-only `createRoot` in main.tsx), which is
  // the one situation where `useLayoutEffect` warns.
  //
  // THE IN-FLIGHT FLAGS GO TOO, AND THE VISIT COUNTER IS WHAT MAKES THAT SAFE (#861).
  // Leaving `sending` set handed the new family a composer they could not type in and a
  // "thinking" timer started under the previous family's name, for as long as the other
  // family's request took. But clearing a flag that a still-running continuation will clear
  // again is the `localBusy` hazard, and here it is worse than usual: family A's answer
  // lands, its `finally` sets `sending` false, and if family B has since started a send of
  // their own, B's composer re-enables mid-request and a second question goes out. So each
  // continuation below re-checks the visit it was issued under and writes nothing once that
  // has moved on. Clearing here and refusing there are one fix, not two.
  //
  // A VISIT, not a workspace id, deliberately. The id names a family; it does not name the
  // trip. On A -> B -> A with an A request still in flight, `workspaceRef.current` is A's id
  // again, so an id comparison passes: A's answer is appended to the transcript this reset
  // emptied, and its `finally` clears flags belonging to a send that started after the
  // return. A counter bumped on every switch is the same guard with the missing concept in
  // it. It moves exactly when this block resets: ENTERING a null workspace is not a visit
  // (nothing is cleared, so nothing in flight is orphaned), LEAVING one is (the transcript is
  // emptied on the way out, so a request issued before it belongs to nobody either way).
  const workspaceRef = useRef<string | null | undefined>(undefined);
  const visitRef = useRef(0);
  useLayoutEffect(() => {
    const prev = workspaceRef.current;
    workspaceRef.current = activeWorkspaceId;
    if (prev !== undefined && prev !== activeWorkspaceId && activeWorkspaceId) {
      visitRef.current += 1;
      setMessages([]);
      setHeldThread(null);
      setInput('');
      setHeldBudget(null);
      setSending(false);
      setStoppable(false);
      setSendStartedAt(null);
      setLoadingThread(false);
    }
  }, [activeWorkspaceId]);

  const openPanel = useCallback(() => setPanelRequested(true), []);
  const closePanel = useCallback(() => setPanelRequested(false), []);

  // The tour walks anchors on the Dashboard and measures their rects; a docked panel reflows
  // the content column those anchors live in. Close on `starting`, not on `active`: by the
  // time the overlay is up the anchors have already been measured (#510's start poll runs
  // first), so a close at `active` would spotlight where things used to be.
  const tour = useTourOptional();
  const tourStarting = tour?.starting ?? false;
  useEffect(() => {
    if (tourStarting) setPanelRequested(false);
  }, [tourStarting]);

  // Persist open state and the conversation together, never one without the other (P9).
  //
  // `panelRequested`, not `panelOpen`: a narrow window is not a decision to close the panel,
  // and neither is standing on `/ask`; writing `open: false` for either would silently forget
  // it for the next wide window, which is the Back-button bug the derivation above fixes.
  //
  // One edge that buys: a cold load *on* `/ask` now keeps `open: true` with a null thread, so
  // navigating away afterwards docks an empty panel. That is a fresh chat, not the "my chat is
  // gone" greeting P9 guards against — the panel and the page share one conversation, so an
  // empty panel here means the session genuinely has none yet.
  //
  // Held until any pending rehydration has been decided. This effect runs at mount, before
  // the remembered thread has been fetched, when `activeThreadId` is still null — writing
  // then would overwrite the very pointer we are about to use. The happy path self-heals
  // (the fetch lands and writes the id back), but a failed fetch, or a reload inside that
  // window, would lose the conversation permanently. That is exactly the "my chat is gone"
  // greeting the paired write exists to prevent.
  //
  // The two halves of the record come from ONE view of the world (#862). `activeThreadId` is
  // derived in render from the workspace its pointer is held under, so it is already null in
  // the commit that flipped `activeWorkspaceId` — there is no ordering left for this write
  // to get wrong, and no moment at which storage holds one family's thread under another
  // family's id for a tab to die inside.
  useEffect(() => {
    if (!rehydrateSettled) return;
    writePanelState({ open: panelRequested, threadId: activeThreadId, workspaceId: activeWorkspaceId });
  }, [rehydrateSettled, panelRequested, activeThreadId, activeWorkspaceId]);

  // Truthy non-empty string only (docs/15 C3) — `null`/absent means not acknowledged. An ack
  // from an older consent substance (version below current) re-prompts; absent version = 1.
  //
  // Two independent pairs (steward-app#2523): once the kind is known, the active workspace's kind
  // picks which pair counts, so a household acceptance does not satisfy the firm dialog and a
  // firm one does not satisfy the household dialog. `firmConversation` is `isFirmWorkspace`'s
  // answer (the same source as `savesChats`) and reads `false` while the kind is unresolved,
  // which would let a firm user with only a family acknowledgment send. So while `kindUnresolved`
  // is true (the caller's definition is in `useClientFrame`: no membership yet, or a degraded
  // packs read) nothing counts as acknowledged and send stays disabled, and `profileLoaded`
  // below stays false so neither dialog is offered. It is a client-side gate; the server decides
  // the kind from the packs on its own.
  //
  // The rule itself is `hasAiConsent` (steward-types), the one the façade enforces in
  // `beginTurn` (steward-app#2542), so the two cannot disagree about what counts.
  const consentAcknowledged = !kindUnresolved && hasAiConsent(
    profile?.preferences as Record<string, unknown> | undefined,
    firmConversation ? 'firm' : 'family',
  );

  // Both queries below follow the pattern `src/feedback/useFeedback.ts` sets for a query
  // mounted from `Layout` rather than from a page:
  //
  // - Gated on a resolved workspace, not just a token. `engaged` is sticky by design, so
  //   after one /ask visit these stay mounted for the session — including on
  //   /select-workspace, where there is no active workspace and the answer would be a 403
  //   the member can do nothing about.
  // - A real `staleTime`. The app default is 30s, which is a page-lifetime window, not a
  //   session-lifetime one: with `refetchOnWindowFocus` on, an alt-tab back into the app
  //   from any route would otherwise re-fetch both for the rest of the session.

  // Seed the meter from billing config (also refreshed live by each answer's usage).
  // 60s matches `ConnectedAccounts.tsx`, the other observer of this exact key — a shorter
  // window here would just win over it and quietly undo its budget.
  const { data: billing } = useQuery({
    queryKey: workspaceKey('billing-config', activeWorkspaceId),
    queryFn: () => createApiClient(token!).get<BillingAiUsage>('/billing/config'),
    enabled: !!token && !!activeWorkspaceId && engaged,
    staleTime: 60_000,
  });

  // The caller's own saved threads (rolling retention). Every in-app change to this list
  // (send, delete) invalidates the key explicitly, so freshness never depends on the window
  // below — it only bounds how long another tab's edits take to appear.
  //
  // Fetched in a firm workspace too since 2026-10-26 (steward-app#1817). The façade answers
  // with only the threads of the workspace's own kind, so a firm list never shows a household
  // thread. The key is per workspace, so a switch never shows the other kind's cached list.
  const { data: threads = [] } = useQuery({
    queryKey: workspaceKey('assistant-threads', activeWorkspaceId),
    queryFn: () => createApiClient(token!).get<{ items: ChatThreadSummary[] }>('/assistant/threads').then((r) => r.items),
    enabled: !!token && !!activeWorkspaceId && engaged,
    staleTime: 5 * 60_000,
  });

  // Whether to stream answers (docs/41 §9, steward-app#2548). Read once the surface is engaged,
  // like the two reads above. ANY failure (a 404 from a server that has not deployed the route,
  // a 5xx, a network error, a malformed answer) reads as "off", which is today's buffered path,
  // so this read can never make the assistant worse. It never throws: nothing waits on it, and
  // a send made before it settles simply goes the buffered way.
  const { data: assistantConfig } = useQuery({
    queryKey: workspaceKey('assistant-config', activeWorkspaceId),
    queryFn: async (): Promise<AssistantConfig> => {
      try {
        return parseAssistantConfig(await createApiClient(token!).get<unknown>('/assistant/config'));
      } catch {
        return STREAMING_OFF;
      }
    },
    enabled: !!token && !!activeWorkspaceId && engaged,
    staleTime: 60_000,
    retry: false,
  });

  // Seed the meter once per family, and never from the family you just left (#859).
  //
  // Three guards, and each one is load bearing:
  //
  // - `budget` — stop the meter thrashing. Every answer replaces it with that turn's own
  //   usage, which is fresher than a config read up to 60s old, so a re-seed would walk it
  //   backwards. `budget` is derived from the workspace its numbers were seeded for, so a
  //   switch makes it null in the same render that flipped the workspace and this effect is
  //   free to seed the new family — without the bail ever being loosened.
  // - THE CONFIG IS READ BY KEY, not taken from `billing`. This is the guard the race needs
  //   and it is the whole of #859, restated for #845.
  //
  //   The original guard was inferential: a `seenUnder` ref recording which family a config
  //   OBJECT was first handed to us under, on the reasoning that an object first seen under
  //   family X was fetched for X. That held while ['billing-config'] was one shared,
  //   workspace-agnostic entry. It stopped holding the moment the entries were split per
  //   workspace, and the failure was measured, not predicted: on an A -> B -> A round trip,
  //   B's config can resolve AFTER the return to A, arrive as a NEW object, be first seen in
  //   a render whose `activeWorkspaceId` is already A, and be stamped as A's. The advisor
  //   then reads B's usage under A's name — exactly #859, wearing the fix's own clothes.
  //
  //   Reading `queryClient.getQueryData(workspaceKey('billing-config', activeWorkspaceId))`
  //   cannot make that mistake, because the key names the family. There is no inference left
  //   to get wrong and no ordering to lose: a late response for another workspace lands in
  //   another entry, which this never reads. `billing` stays in the dependency list purely
  //   as the signal that something arrived.
  // - `aiTokensUsed` — a config that carries no usage is not a seed.
  //
  // WHAT THIS NO LONGER DEPENDS ON. It used to depend, across files, on WorkspaceProvider's
  // switch reset dropping ['billing-config'] so the object identity would change. It does
  // not any more: the entry this reads is this family's own, whether or not that reset fires
  // (steward-app#845). `AssistantProvider.switchResidue.test.tsx` still asserts the switch
  // refetches the config, because the METER still has to be seeded from something current.
  useEffect(() => {
    // Mirror the query's own gate: with no resolved workspace there is no entry to read.
    if (!activeWorkspaceId) return;
    if (budget) return;
    const config = qc.getQueryData<BillingAiUsage>(workspaceKey('billing-config', activeWorkspaceId));
    if (!config || config.aiTokensUsed === undefined) return;
    setHeldBudget({
      value: {
        used: config.aiTokensUsed,
        quota: config.aiTokenQuota ?? null,
        remaining: config.aiTokensRemaining ?? null,
        allowed: config.aiTokenQuota == null || config.aiTokensUsed < config.aiTokenQuota,
      },
      workspaceId: activeWorkspaceId,
    });
  }, [billing, budget, activeWorkspaceId, qc]);

  const limitReached = budget != null && !budget.allowed;

  const acceptConsent = useCallback(async () => {
    await acknowledgeAiConsent(firmConversation ? 'firm' : 'family');
  }, [acknowledgeAiConsent, firmConversation]);

  const startNewChat = useCallback(() => {
    setHeldThread(null);
    setMessages([]);
    setInput('');
  }, []);

  const openThread = useCallback(async (id: string) => {
    if (!token || id === activeThreadId) return;
    // The visit this fetch was issued during, and the family it belongs to. See the reset
    // above for why the guard counts visits rather than comparing workspace ids.
    const sentUnder = visitRef.current;
    const workspaceId = activeWorkspaceId;
    setLoadingThread(true);
    try {
      const thread = await createApiClient(token).get<ChatThread>(`/assistant/threads/${id}`);
      if (visitRef.current !== sentUnder) return; // switched mid-flight; see below
      setHeldThread({ value: thread.id, workspaceId });
      setMessages(thread.messages.map((m) => ({ id: nextId.current++, role: m.role, text: m.text })));
    } catch {
      if (visitRef.current !== sentUnder) return;
      setMessages([{ id: nextId.current++, role: 'error', text: "We couldn't load that chat. Please try again." }]);
    } finally {
      // The spinner belongs to this visit too: the switch already cleared it for the family
      // now on screen, and clearing it again could retire a spinner they started themselves.
      if (visitRef.current === sentUnder) setLoadingThread(false);
    }
  }, [token, activeThreadId, activeWorkspaceId]);

  // Bring back the conversation the restored-open panel is showing an empty box for (P9).
  //
  // Runs at most once, and only for a panel that restored itself open — a user who opens the
  // panel by hand this session is starting a new chat, not resuming one. Deliberately NOT
  // `openThread`: a thread deleted since (from another device, or by retention) would put
  // "We couldn't load that chat" in a panel the user never asked to open. A failure here
  // leaves an empty new chat, which is what an unremembered panel would have shown anyway.
  const rehydrateStarted = useRef(false);
  useEffect(() => {
    const { threadId, workspaceId } = restored.current;
    if (rehydrateSettled || rehydrateStarted.current) return;
    // A cold load of /ask is a fresh page, as it has always been. Resuming the panel's
    // remembered thread there would silently continue a conversation the user did not open,
    // and the next send would extend it.
    if (pathname === '/ask') { setRehydrateSettled(true); return; }
    if (!token || !activeWorkspaceId) return;         // wait for the session to resolve
    if (workspaceId !== activeWorkspaceId) {          // stored under a different workspace
      setRehydrateSettled(true);
      return;
    }
    rehydrateStarted.current = true;
    const sentUnder = visitRef.current;
    const belongsTo = activeWorkspaceId;
    setLoadingThread(true);
    void createApiClient(token).get<ChatThread>(`/assistant/threads/${threadId}`)
      .then((thread) => {
        if (visitRef.current !== sentUnder) return;
        setHeldThread({ value: thread.id, workspaceId: belongsTo });
        setMessages(thread.messages.map((m) => ({ id: nextId.current++, role: m.role, text: m.text })));
      })
      .catch(() => { /* start fresh; see above */ })
      .finally(() => {
        // The spinner is this visit's; `rehydrateSettled` is this MOUNT's. The persist effect
        // is held until the latter flips, so it has to flip whatever the workspace has done
        // meanwhile, or the panel's remembered pointer would never be written again.
        if (visitRef.current === sentUnder) setLoadingThread(false);
        setRehydrateSettled(true);
      });
  }, [token, activeWorkspaceId, pathname, rehydrateSettled]);

  const deleteThread = useCallback(async (id: string) => {
    if (!token) return;
    // The fourth continuation, and the same guard for the same reason (#861 review). Without
    // it, deleting a chat and switching family before the DELETE returns runs `startNewChat`
    // against the family now on screen: `activeThreadId` is captured from the render this
    // callback was built in, so the comparison still matches, and it empties a transcript and
    // a half-typed draft belonging to someone else.
    const sentUnder = visitRef.current;
    try {
      await createApiClient(token).delete(`/assistant/threads/${id}`);
      if (id === activeThreadId && visitRef.current === sentUnder) startNewChat();
      void qc.invalidateQueries({ queryKey: workspaceKey('assistant-threads', activeWorkspaceId) });
    } catch {
      // best-effort; the list will reconcile on next load
      void qc.invalidateQueries({ queryKey: workspaceKey('assistant-threads', activeWorkspaceId) });
    }
  }, [token, activeThreadId, startNewChat, qc, activeWorkspaceId]);

  const renameThread = useCallback(async (id: string, title: string) => {
    if (!token) throw new Error(RENAME_FAILED_MESSAGE);
    // The same guard as `deleteThread`. A rename that returns after a family switch belongs
    // to a list no longer on screen: its invalidation targets the old workspace's key (harmless)
    // and its refusal is swallowed rather than shown in a dialog about someone else's chat.
    const sentUnder = visitRef.current;
    const invalidate = () => qc.invalidateQueries({ queryKey: workspaceKey('assistant-threads', activeWorkspaceId) });
    try {
      await createApiClient(token).put(`/assistant/threads/${encodeURIComponent(id)}`, { title });
      void invalidate();
    } catch (err) {
      void invalidate();
      if (visitRef.current !== sentUnder) return;
      if (err instanceof ApiError && err.status === 409) throw new Error(RENAME_CONFLICT_MESSAGE);
      throw new Error(RENAME_FAILED_MESSAGE);
    }
  }, [token, qc, activeWorkspaceId]);

  // The question in flight, so Stop can reach it. One at a time: `sending` blocks a second send.
  // `streamed` is true while the turn is on the streaming transport, firm or household. `cancel`
  // tells the server to stop that run (docs/41 §5.2 (b)); null on a turn that never streams.
  const pendingRef = useRef<{ controller: AbortController; userMsgId: number; question: string; visit: number; provisionalId: number | null; streamed: boolean; cancel: (() => void) | null } | null>(null);

  const stop = useCallback(() => {
    const pending = pendingRef.current;
    // A pending from another visit was already cleared by the switch reset; leave it alone.
    // Nor a turn that is not streamed: the buffered route saves the exchange regardless (see
    // the `stop` docblock on the context type).
    if (!pending || !pending.streamed || pending.visit !== visitRef.current) return;
    pendingRef.current = null;
    // The cancel first, so it is on its way before the abort. Fire-and-forget: nothing waits on
    // it and a failure shows nothing (see the `stop` docblock).
    pending.cancel?.();
    // Aborting is what makes the late response and the abort's own rejection inert: `send`
    // checks `controller.signal.aborted` after every await. The flags are cleared HERE, not in
    // `send`'s finally, because the person may send again before that finally runs.
    pending.controller.abort();
    setMessages((m) => m.filter((x) => x.id !== pending.userMsgId && x.id !== pending.provisionalId));
    setInput(pending.question);
    setSending(false);
    setStoppable(false);
    setSendStartedAt(null);
  }, []);

  const send = useCallback(async (text?: string) => {
    const question = (text ?? input).trim();
    // No active workspace means no family to ask about: the façade refuses a scoped request
    // without the header, and the two queries above are gated the same way. Fails closed
    // rather than sending a question that can only come back an error.
    if (!question || sending || limitReached || !token || !consentAcknowledged || !activeWorkspaceId) return;
    // The visit this turn was asked during, and the family it was asked of. An answer that
    // arrives after a switch belongs to the conversation the reset above already cleared, so
    // it is dropped rather than appended to (or worse, metered against) the workspace now on
    // screen — and that includes the in-flight flags, which a new send may already own.
    const sentUnder = visitRef.current;
    const workspaceId = activeWorkspaceId;
    const userMsg: Message = { id: nextId.current++, role: 'user', text: question };
    const controller = new AbortController();
    const streamUrl = assistantConfig?.streaming ? assistantConfig.streamUrl : undefined;
    // Minted at send time so a Stop can name this run before any `start` line arrives (docs/41
    // §4.2). The cancel is bound to this send's token, not whatever token is current at Stop.
    const runId = crypto.randomUUID();
    const pending = {
      controller, userMsgId: userMsg.id, question, visit: sentUnder, provisionalId: null as number | null,
      streamed: !!streamUrl,
      cancel: streamUrl ? () => {
        void createApiClient(token).post<null>('/assistant/query/cancel', { runId }, { timeoutMs: ASSISTANT_CANCEL_TIMEOUT_MS })
          .catch(() => {});
      } : null,
    };
    pendingRef.current = pending;
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSendThreadId(activeThreadId);
    setSending(true);
    setStoppable(pending.streamed);
    const startedAt = Date.now();
    setSendStartedAt(startedAt);
    // The provisional answer shown while a stream is open: one assistant message, created on the
    // first delta, emptied away on `reset`, replaced by the real answer on `done`, and removed on
    // any failure. Every write is guarded by the visit and by Stop, like the rest of this send.
    // Its id is taken lazily, so a buffered send consumes exactly the ids it always did.
    const showProvisional = (provisional: string, kind: string | undefined) => {
      if (visitRef.current !== sentUnder || controller.signal.aborted) return;
      const provisionalId = (pending.provisionalId ??= nextId.current++);
      // Err strict: the provisional text is firm-rendered (in-app links only) unless the stream's
      // own `start` line said household. A stale workspace-kind hint can then only make a bubble
      // stricter than needed, never stream a firm answer through the loose renderer.
      const strict = firmConversation || kind !== 'household';
      setMessages((m) => {
        const without = m.filter((x) => x.id !== provisionalId);
        return provisional
          ? [...without, { id: provisionalId, role: 'assistant', text: provisional, ...(strict ? { firm: true } : {}) }]
          : without;
      });
    };
    const dropProvisional = () => {
      const id = pending.provisionalId;
      if (id !== null) setMessages((m) => m.filter((x) => x.id !== id));
    };
    try {
      const body = {
        question,
        // Continue the saved thread, for either kind (steward-app#1817: firm conversations are
        // saved too since 2026-10-26). No `history` is sent: the server replays the stored
        // thread, and ignores a `history` key if an older bundle sends one.
        ...(activeThreadId ? { threadId: activeThreadId } : {}),
        // The current screen, so the assistant can resolve "this account" without being
        // told twice (docs/28 D1). `pathname` only: react-router already splits the query
        // string and fragment into `search`/`hash`, and those are page CONTENT (a search
        // box, a filter set), not identity — they must never travel. Omitted entirely on
        // `/ask` (D9): the page IS the assistant there, so sending it would invite the
        // model to answer questions about the chat screen. Same signal `panelOpen` above
        // already derives its suppression from — not a second way to ask the question.
        ...(pathname !== '/ask' ? { path: pathname } : {}),
        // The asker's time zone, so the assistant's "as of" date is their local date
        // (steward-app#2526). Household only (the firm assistant's dates come from snapshot
        // data), and optional: omitted if the browser cannot say.
        ...(firmConversation ? {} : (() => {
          try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return timeZone ? { timeZone } : {};
          } catch {
            return {};
          }
        })()),
      };
      const client = createApiClient(token);
      const askBuffered = () => client.post<QueryResponse>('/assistant/query', body, {
        timeoutMs: ASSISTANT_QUERY_TIMEOUT_MS, ...(firmConversation ? { signal: controller.signal } : {}),
      });
      const res = streamUrl
        ? await askStreamed({
            // The `runId` minted above, so the cancel `stop` sends names this run.
            open: () => client.postStream(streamUrl, { ...body, runId }, {
              timeoutMs: ASSISTANT_QUERY_TIMEOUT_MS, signal: controller.signal,
            }),
            // The stream could not be used, so this turn is now buffered: no Stop from here on.
            buffered: () => {
              pending.streamed = false;
              if (visitRef.current === sentUnder && !controller.signal.aborted) setStoppable(false);
              return askBuffered();
            },
            signal: controller.signal,
            onText: showProvisional,
          })
        : await askBuffered();
      if (visitRef.current !== sentUnder || controller.signal.aborted) return;
      // Prefer the server-measured model latency; fall back to the client round-trip.
      const meta: MessageMeta = { thinkMs: res.latencyMs ?? Date.now() - startedAt };
      // Firm answers render their links strictly (see `firmMarkdownComponents`). Either
      // signal is enough, so a hint that lags a workspace switch can only err strict.
      const firm = res.kind === 'firm' || firmConversation;
      dropProvisional();
      setMessages((m) => [...m, { id: nextId.current++, role: 'assistant', text: res.answer, meta, ...(firm ? { firm: true } : {}) }]);
      setHeldBudget({ value: res.usage, workspaceId });
      if (res.threadId) setHeldThread({ value: res.threadId, workspaceId });
      // Surface the new/updated thread in the rail (title + ordering).
      void qc.invalidateQueries({ queryKey: workspaceKey('assistant-threads', activeWorkspaceId) });
    } catch (err) {
      // A Stop is not a failure: `stop` has already restored the question and the flags.
      if (visitRef.current !== sentUnder || controller.signal.aborted) return;
      // The façade refused this caller the assistant here, on appcore's answered `false`
      // (steward-app#1814). Not a chat error: record it as the capability answer, which moves
      // every surface onto `AssistantUnavailable`, and take back the question it will now
      // never answer. A 503 `ASSISTANT_CHECK_FAILED` (the server could not check) is NOT
      // this: it falls through to the ordinary error below, keeping the question to retry.
      if (err instanceof ApiError && err.status === 403 && err.code === ASSISTANT_NOT_AVAILABLE_CODE) {
        markAssistantUnavailable(qc, workspaceId);
        setMessages((m) => m.filter((x) => x.id !== userMsg.id));
        return;
      }
      // The façade accepted no AI consent acknowledgement for this question (steward-app#2542).
      // Re-read the profile: if this tab's copy was stale, `consentAcknowledged` turns false and
      // the notice is offered again. Either way the failed turn shows the server's message
      // below and the question goes back in the input, so it can be sent once accepted.
      const consentRequired = err instanceof ApiError && err.status === 403 && err.code === AI_CONSENT_REQUIRED_CODE;
      if (consentRequired) void refresh?.();
      dropProvisional();
      const msg = err instanceof ApiError ? err.message
        : err instanceof StreamFailure && err.display ? err.display
        : 'Something went wrong. Please try again.';
      // A stream that failed after it started leaves the question in the input, as Stop does
      // (docs/41 §4.3). Only when the person has not typed something else in the meantime.
      if (err instanceof StreamFailure || consentRequired) setInput((cur) => cur || question);
      setMessages((m) => [...m, { id: nextId.current++, role: 'error', text: msg }]);
    } finally {
      // Only this visit's flags. The switch cleared them already; writing them again here
      // would hand the family now on screen the state of a request they never made — and if
      // they have started a send of their own, re-enable their composer mid-request.
      // Nor a send that was stopped: its flags were cleared by `stop`, and a newer send may own them.
      if (pendingRef.current?.controller === controller) pendingRef.current = null;
      if (visitRef.current === sentUnder && !controller.signal.aborted) {
        setSending(false);
        setStoppable(false);
        setSendStartedAt(null);
      }
    }
  }, [input, sending, limitReached, token, consentAcknowledged, activeThreadId, activeWorkspaceId, qc, pathname, firmConversation, assistantConfig, refresh]);

  const value = useMemo<AssistantContextValue>(() => ({
    messages, activeThreadId, threads, sending, sendingThreadId: sending ? sendThreadId : null, sendStartedAt, loadingThread,
    input, setInput,
    budget, limitReached,
    consentAcknowledged, profileLoaded: !!profile && !kindUnresolved, kindLoadFailed, retryKind, acceptConsent,
    send, stop, canStop: stoppable, startNewChat, openThread, deleteThread, renameThread, savesChats: true, firmConversation,
    panelOpen, canDockPanel, openPanel, closePanel,
  }), [
    messages, activeThreadId, threads, sending, sendThreadId, sendStartedAt, loadingThread, input,
    budget, limitReached, consentAcknowledged, profile, kindUnresolved, kindLoadFailed, retryKind, acceptConsent,
    send, stop, stoppable, startNewChat, openThread, deleteThread, renameThread, firmConversation,
    panelOpen, canDockPanel, openPanel, closePanel,
  ]);

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}
